import {dissocPath, isNil, omit} from 'ramda'

import {capitalizeFirstLetter} from '../../utils/string'
import {
  AnalyticsContext,
  TrackEventPayload,
  TrackEventProperties
} from '../types/Events'

/**
 * Recursively traverses a nested structure of objects and arrays,
 * replacing any array that consists **only** of objects with `null`.
 *
 * This cleaning helps avoid FullStory serialization errors caused by arrays of objects.
 * @param data - The nested data to clean, which can be primitives, arrays, or objects.
 * @returns The cleaned nested data, where arrays of only objects are replaced by `null`.
 */
export const clearObjectArraysFromAnalyticsContext = (
  data: TrackEventPayload
): TrackEventPayload => {
  const cleanedAnalyticsContext = clean(data.analyticsContext)

  return {
    ...data,
    analyticsContext: cleanedAnalyticsContext ?? undefined
  }

  function clean(value: unknown): unknown {
    if (value === undefined || value === null) return value

    if (Array.isArray(value)) {
      return null
    }

    if (typeof value === 'object') {
      const cleanedObj: Record<string, unknown> = {}
      for (const key in value as Record<string, unknown>) {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          cleanedObj[key] = clean((value as Record<string, unknown>)[key])
        }
      }
      return cleanedObj
    }

    return value
  }
}

/**
 * Generates a standardized event name for the data pipeline
 * by capitalizing the first letter of the category and appending "Event".
 * @param trackEventPayload - The payload containing tracking event details, including the category.
 * @returns A formatted string used as the data pipeline event name.
 */
export const createDataPipelineEventName = (
  trackEventPayload: TrackEventPayload
) => `${capitalizeFirstLetter(String(trackEventPayload.category))}Event`

/**
 * Increases the given position index by one, unless it is `null`, `undefined`, or `-1`,
 * in which case it returns `null`.
 * @param position - The original position index (optional).
 * @returns The incremented position, or `null` if the input is `null`, `undefined`, or `-1`.
 */
export const increasePositionIndexByOne = (position?: number) => {
  if (isNil(position) || position === -1) return null
  return position + 1
}

/**
 * Removes the `SearchResultsContext` entry from the `analyticsContext` property
 * within a track event object.
 * @param trackEventProperties - The original track event payload.
 * @returns A new `TrackEventPayload` object with `SearchResultsContext` removed.
 */
export const removeSearchResultsContext = (
  trackEventProperties: TrackEventProperties
): TrackEventPayload => {
  return dissocPath(
    ['analyticsContext', AnalyticsContext.SearchResultsContext],
    trackEventProperties
  )
}

/**
 * Removes the entire `analyticsContext` property from the given track event payload.
 * @param trackEventProperties - The original track event payload.
 * @returns A new `TrackEventPayload` object without the `analyticsContext` property.
 */
export const removeAllAnalyticsContexts = (
  trackEventProperties: TrackEventProperties
): TrackEventPayload => {
  return omit(['analyticsContext'], trackEventProperties)
}
